import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import Content from "./components/content.jsx";
import {Card} from "../../../components/card/card.jsx";
import {subDays} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subDays(new Date(), 7),
    dtEnd: new Date(),
    hrStart: new Date(new Date().setHours(0, 0)),
    hrEnd: new Date(new Date().setHours(23, 59)),
    period: { label: "Todos", value: "all" },
    dispatch: { label: "Todos", value: "all" },
    options: [],
    user: [],
    dispatchList: false
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    hrStart: "Hora inicial",
    hrEnd: "Hora final",
    period: "Período",
    dispatch: "Despacho",
    options: "Indicadores de preenchimento",
    user: "Agentes",
    dispatchList: "Listar despachos"
};

const DispatchQuality = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);

    const cardsData = reportData?.apply?.cards?.[0]?.value_data ? JSON.parse(reportData.apply.cards[0].value_data) : {};
    const hasData = cardsData.total ?? 0;

    const [title] = useState("Qualidade do despacho");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
                hrStart: filters.hrStart ? ReportUtils.formatHourMin(filters.hrStart, false) : "",
                hrEnd: filters.hrEnd ? ReportUtils.formatHourMin(filters.hrEnd, false) : "",
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null);
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!ReportUtils.requiredFilterValidate(filters.options, "Indicadores de preenchimento", setLoading)) return;
            if (!ReportUtils.validateHourOnly(filters.hrStart, filters.hrEnd, setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.period, "Período", setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                hrStart: filters.hrStart ? ReportUtils.formatHourMin(filters.hrStart, false) : null,
                hrEnd: filters.hrEnd ? ReportUtils.formatHourMin(filters.hrEnd, false) : null,
                period: filters.period.value ?? 'all',
                dispatch: filters.dispatch.value ?? 'all',
                options: filters.options?.map(i => i.value) ?? [],
                user: filters.user?.map(i => i.value) ?? [],
                dispatchList: filters.dispatchList ?? false
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                hrStart: formattedFilters.hrStart,
                hrEnd: formattedFilters.hrEnd
            }, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/dispatch_quality/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={hasData}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                                setCollapsed={setCollapsed}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters} hasSeconds={false}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || hasData === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || hasData === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || hasData === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={hasData}/>
                            <Content reportData={reportData} data={hasData} dispatchList={appliedFilters.dispatchList} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </>
    );
};

export default DispatchQuality;